import { useFormContext } from 'react-hook-form';
import { DatePickerInput } from '@/components/common/DatePickerInput';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Switch } from '@/components/ui/switch';
import type { PcmpProjectFormValues } from '../../types/pcmpProjectForm.schema';
import { PcmpFormSection } from './PcmpFormSection';

type FlagName = 'isActive' | 'isWebVisible' | 'isMobileVisible';

const FLAGS: { name: FlagName; label: string; description: string }[] = [
  {
    name: 'isActive',
    label: 'Active',
    description: 'Inactive projects take no new submissions.',
  },
  { name: 'isWebVisible', label: 'Show on web', description: 'Offered on the web app.' },
  {
    name: 'isMobileVisible',
    label: 'Show on mobile',
    description: 'Offered to MOs in the mobile app.',
  },
];

export function PcmpProjectAvailabilitySection() {
  const { control } = useFormContext<PcmpProjectFormValues>();

  return (
    <PcmpFormSection id="pcmp-project-availability-heading" title="Availability">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField
          control={control}
          name="activeFrom"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>
                Active From <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <DatePickerInput
                  id="pcmp-project-active-from"
                  value={field.value}
                  onChange={field.onChange}
                  className="sm:h-10"
                  {...(fieldState.invalid ? { 'aria-invalid': true } : {})}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="activeTo"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>
                Active To <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <DatePickerInput
                  id="pcmp-project-active-to"
                  value={field.value}
                  onChange={field.onChange}
                  className="sm:h-10"
                  {...(fieldState.invalid ? { 'aria-invalid': true } : {})}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="flex flex-col divide-y rounded-lg border">
        {FLAGS.map((flag) => (
          <FormField
            key={flag.name}
            control={control}
            name={flag.name}
            render={({ field }) => (
              <FormItem className="flex-row items-center justify-between gap-4 px-4 py-3">
                <div className="flex flex-col gap-1">
                  <span className="text-sm leading-none font-medium">{flag.label}</span>
                  <FormDescription>{flag.description}</FormDescription>
                  {flag.name === 'isMobileVisible' ? <FormMessage /> : null}
                </div>
                <FormControl>
                  <Switch
                    aria-label={flag.label}
                    checked={field.value}
                    onCheckedChange={(checked) => field.onChange(checked)}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        ))}
      </div>
    </PcmpFormSection>
  );
}
